import { useState } from 'react';
import {
  Button,
  ConfirmDialog,
  Group,
  BottomDrawer,
  PageHeader,
  Paper,
  ResourceLayout,
  Select,
  Stack,
  Text,
  TextInput,
  type ResourceQuery,
} from '@vipso/shared';
import {
  useCreateSystemAdminMutation,
  useDeleteSystemAdminMutation,
  useGetMasterRolesQuery,
  useGetSystemAdminsQuery,
  useUpdateSystemAdminMutation,
  type SystemAdminRecord,
} from '../api/adminApi';

export function StaffPage() {
  const [query, setQuery] = useState<ResourceQuery>({
    page: 1,
    pageLimit: 20,
    search: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SystemAdminRecord | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SystemAdminRecord | null>(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    masterRoleId: '',
  });

  const { data, isLoading, isFetching } = useGetSystemAdminsQuery({
    page: query.page,
    pageLimit: query.pageLimit,
    search: query.search || undefined,
  });
  const { data: rolesData } = useGetMasterRolesQuery();
  const [createSystemAdmin, createState] = useCreateSystemAdminMutation();
  const [updateSystemAdmin, updateState] = useUpdateSystemAdminMutation();
  const [deleteSystemAdmin, deleteState] = useDeleteSystemAdminMutation();

  const list = data?.systemAdmins;
  const roleOptions = (rolesData?.masterRoles ?? []).map((r) => ({
    value: r.id,
    label: r.name,
  }));

  function resetForm() {
    setForm({ name: '', email: '', phone: '', password: '', masterRoleId: '' });
    setEditing(null);
  }

  function openEdit(row: SystemAdminRecord) {
    setEditing(row);
    setForm({
      name: row.name,
      email: row.email,
      phone: row.phone || '',
      password: '',
      masterRoleId: row.masterRoleId || row.masterRole?.id || '',
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (editing) {
      await updateSystemAdmin({
        id: editing.id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        masterRoleId: form.masterRoleId || null,
      }).unwrap();
    } else {
      await createSystemAdmin({
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password,
        phone: form.phone.trim() || undefined,
        masterRoleId: form.masterRoleId || undefined,
      }).unwrap();
    }
    resetForm();
    setModalOpen(false);
  }

  const saving = createState.isLoading || updateState.isLoading;

  return (
    <>
      <PageHeader
        title="System staff"
        description="Manage master admin accounts."
      />

      <ResourceLayout
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        onQueryChange={setQuery}
        getRowId={(row) => row.id}
        searchable
        searchPlaceholder="Search staff..."
        emptyTitle="No staff members"
        emptyDescription="Add a system admin to grant platform access."
        addButton={
          <Button
            onClick={() => {
              resetForm();
              setModalOpen(true);
            }}
          >
            Add staff member
          </Button>
        }
        columns={[
          { key: 'id', header: 'ID', render: (row) => row.id },
          { key: 'name', header: 'Name', render: (row) => row.name },
          { key: 'email', header: 'Email', render: (row) => row.email },
          { key: 'phone', header: 'Phone', render: (row) => row.phone ?? '—' },
          {
            key: 'role',
            header: 'Role',
            render: (row) => row.masterRole?.name ?? '—',
          },
          {
            key: 'actions',
            header: 'Actions',
            render: (row) => (
              <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                <Button size="xs" variant="default" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => setPendingDelete(row)}
                >
                  Delete
                </Button>
              </Group>
            ),
          },
        ]}
        onRowClick={openEdit}
        renderCard={(row: SystemAdminRecord) => (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Stack gap={2}>
                <Text fw={600}>{row.name}</Text>
                <Text size="sm" c="dimmed">
                  {row.email}
                </Text>
                <Text size="sm">{row.masterRole?.name ?? 'No role'}</Text>
              </Stack>
              <Group gap="xs" onClick={(e) => e.stopPropagation()}>
                <Button size="xs" variant="default" onClick={() => openEdit(row)}>
                  Edit
                </Button>
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => setPendingDelete(row)}
                >
                  Delete
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}
      />

      <BottomDrawer
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit staff member' : 'Create system admin'}
      >
        <Stack gap="md">
          <TextInput
            label="Name"
            value={form.name}
            onChange={(e) => {
              const name = e.currentTarget.value;
              setForm((f) => ({ ...f, name }));
            }}
            required
          />
          <TextInput
            label="Email"
            type="email"
            value={form.email}
            onChange={(e) => {
              const email = e.currentTarget.value;
              setForm((f) => ({ ...f, email }));
            }}
            required
          />
          <TextInput
            label="Phone"
            value={form.phone}
            onChange={(e) => {
              const phone = e.currentTarget.value;
              setForm((f) => ({ ...f, phone }));
            }}
          />
          <Select
            label="Master role"
            placeholder="Optional"
            clearable
            value={form.masterRoleId || null}
            onChange={(value) => setForm((f) => ({ ...f, masterRoleId: value || '' }))}
            data={roleOptions}
          />
          {!editing ? (
            <TextInput
              label="Password"
              type="password"
              value={form.password}
              onChange={(e) => {
                const password = e.currentTarget.value;
                setForm((f) => ({ ...f, password }));
              }}
              required
            />
          ) : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={saving}
              onClick={() => void handleSave()}
              disabled={
                !form.name.trim() ||
                !form.email.trim() ||
                (!editing && !form.password.trim())
              }
            >
              Save
            </Button>
          </Group>
        </Stack>
      </BottomDrawer>

      <ConfirmDialog
        opened={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return;
          await deleteSystemAdmin({ id: pendingDelete.id }).unwrap();
          setPendingDelete(null);
        }}
        title="Delete staff member"
        message={
          pendingDelete ? `Delete "${pendingDelete.name}" (${pendingDelete.email})?` : ''
        }
        confirmLabel="Delete"
        loading={deleteState.isLoading}
        danger
      />
    </>
  );
}
